'use client';

import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { toggleVariants } from './toggle';

// gloss
const toggleGroupVariants = cva('flex items-center justify-start gap-1', {
  variants: {
    variant: {
      default: '',
      outline: '',
      primary: '',
      segmented: 'gap-0 rounded-lg bg-muted p-1',
    },
    width: {
      auto: '',
      full: 'w-full',
    },
  },
  defaultVariants: { variant: 'default', width: 'auto' },
});

type ToggleGroupContextValue = VariantProps<typeof toggleVariants> & VariantProps<typeof toggleGroupVariants>;

const ToggleGroupContext = React.createContext<ToggleGroupContextValue>({
  variant: 'default',
  size: 'default',
  width: 'auto',
});

type ToggleGroupProps = ToggleGroupPrimitive.Props &
  VariantProps<typeof toggleVariants> &
  Pick<VariantProps<typeof toggleGroupVariants>, 'width'> & {
    className?: string;
  };

const ToggleGroup = ({
  className,
  variant = 'default',
  size = 'default',
  width = 'auto',
  children,
  ...props
}: ToggleGroupProps) => {
  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      className={cn(toggleGroupVariants({ variant, width }), className)}
      {...props}
    >
      <ToggleGroupContext value={{ variant, size, width }}>{children}</ToggleGroupContext>
    </ToggleGroupPrimitive>
  );
};

type ToggleGroupItemProps = Toggle.Props &
  VariantProps<typeof toggleVariants> & {
    className?: string;
  };

const ToggleGroupItem = ({ className, variant, size, ...props }: ToggleGroupItemProps) => {
  const context = React.use(ToggleGroupContext);
  const resolvedVariant = variant ?? context.variant ?? 'default';
  const resolvedSize = size ?? context.size ?? 'default';

  return (
    <Toggle
      data-slot="toggle-group-item"
      className={cn(
        toggleVariants({ variant: resolvedVariant, size: resolvedSize }),
        context.width === 'full' && 'flex-1',
        className,
      )}
      {...props}
    />
  );
};

export { ToggleGroup, ToggleGroupItem };
